import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { subDays } from 'date-fns';
import { analytics, ANALYTICS_EVENTS } from '@/lib/analytics';

import { DateRangeSelector } from '@/components/reports/spend/DateRangeSelector';
import { DiversityKPICards } from '@/components/reports/diversity/DiversityKPICards';
import { CertificationMultiSelect } from '@/components/reports/diversity/CertificationMultiSelect';
import { CertificationSpendChart } from '@/components/reports/diversity/CertificationSpendChart';
import { DiversityTrendChart } from '@/components/reports/diversity/DiversityTrendChart';
import { OpportunityFunnelCard } from '@/components/reports/diversity/OpportunityFunnelCard';
import { DiversityByCategoryChart } from '@/components/reports/diversity/DiversityByCategoryChart';
import { DiversityByGeographyChart } from '@/components/reports/diversity/DiversityByGeographyChart';
import { TopDiverseVendorsTable } from '@/components/reports/diversity/TopDiverseVendorsTable';
import { DiversityExportButton } from '@/components/reports/diversity/DiversityExportButton';

import {
  useDiversityCertificationTypes,
  useDiversityKPIs,
  useSpendByCertification,
  useDiversityTrend,
  useOpportunityFunnel,
  useDiversityByCategory,
  useDiversityByGeography,
  useTopDiverseVendors,
  useDiversityExportData,
} from '@/hooks/useDiversityAnalytics';
import { useAuth } from '@/lib/auth';

export default function DiversityReportPage() {
  const { organization } = useAuth();
  const organizationId = organization?.id;

  // Date range state
  const [startDate, setStartDate] = useState(() => subDays(new Date(), 90));
  const [endDate, setEndDate] = useState(() => new Date());

  // Filter state
  const [selectedCertifications, setSelectedCertifications] = useState<string[]>([]);

  const handleDateChange = (start: Date, end: Date) => {
    setStartDate(start);
    setEndDate(end);
  };

  // Track report viewed
  useEffect(() => {
    analytics.track({
      name: ANALYTICS_EVENTS.REPORT_VIEWED,
      category: 'engagement',
      properties: {
        report_type: 'diversity',
      },
    });
  }, []);

  // Fetch certification types for filter
  const { data: certificationTypes, isLoading: loadingCertTypes } = useDiversityCertificationTypes();

  // Fetch all data
  const { data: kpis, isLoading: loadingKPIs } = useDiversityKPIs(
    startDate, endDate, organizationId, [], selectedCertifications
  );
  const { data: certSpend, isLoading: loadingCertSpend } = useSpendByCertification(
    startDate, endDate, organizationId, [], selectedCertifications
  );
  const { data: trend, isLoading: loadingTrend } = useDiversityTrend(
    startDate, endDate, organizationId, [], selectedCertifications
  );
  const { data: funnel, isLoading: loadingFunnel } = useOpportunityFunnel(
    startDate, endDate, organizationId, [], selectedCertifications
  );
  const { data: categoryData, isLoading: loadingCategory } = useDiversityByCategory(
    startDate, endDate, organizationId, [], selectedCertifications
  );
  const { data: geoData, isLoading: loadingGeo } = useDiversityByGeography(
    startDate, endDate, organizationId, [], selectedCertifications
  );
  const { data: vendors, isLoading: loadingVendors } = useTopDiverseVendors(
    startDate, endDate, organizationId, [], selectedCertifications
  );
  const { data: exportJobs, isLoading: loadingExport } = useDiversityExportData(
    startDate, endDate, organizationId, [], selectedCertifications
  );

  const isLoading = loadingKPIs || loadingCertSpend || loadingTrend || loadingFunnel || 
                    loadingCategory || loadingGeo || loadingVendors;

  return (
    <div className="container mx-auto py-8 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/reports">
              <ArrowLeft className="h-5 w-5" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Diversity Report</h1>
            <p className="text-muted-foreground">
              Monitor diversity spend and track progress toward supplier diversity goals
            </p>
          </div>
        </div>
        <DiversityExportButton
          startDate={startDate}
          endDate={endDate}
          selectedCategories={[]}
          selectedCertifications={selectedCertifications}
          kpis={kpis}
          certifications={certSpend}
          trends={trend}
          categories={categoryData}
          geography={geoData}
          vendors={vendors}
          jobs={exportJobs}
          isLoading={isLoading || loadingExport}
        />
      </div>

      {/* Date Range & Filters */}
      <div className="space-y-4">
        <DateRangeSelector
          startDate={startDate}
          endDate={endDate}
          onDateChange={handleDateChange}
        />
        <div className="flex gap-4">
          <CertificationMultiSelect
            certificationTypes={certificationTypes || []}
            selectedIds={selectedCertifications}
            onChange={setSelectedCertifications}
            isLoading={loadingCertTypes}
          />
        </div>
      </div>

      {/* KPI Cards */}
      <DiversityKPICards data={kpis} isLoading={loadingKPIs} />

      {/* Main Charts Row */}
      <div className="grid gap-6 lg:grid-cols-2">
        <CertificationSpendChart data={certSpend} isLoading={loadingCertSpend} />
        <DiversityTrendChart data={trend} isLoading={loadingTrend} />
      </div>

      {/* Second Row */}
      <div className="grid gap-6 lg:grid-cols-2">
        <OpportunityFunnelCard data={funnel} isLoading={loadingFunnel} />
        <DiversityByCategoryChart data={categoryData} isLoading={loadingCategory} />
      </div>

      {/* Geography */}
      <DiversityByGeographyChart data={geoData} isLoading={loadingGeo} />

      {/* Top Vendors Table */}
      <TopDiverseVendorsTable data={vendors} isLoading={loadingVendors} />
    </div>
  );
}
